<template>
    <section v-if="showRoomMapView" class="room-map-view">
        <div class="room-map-view__layout">
            <div class="room-map-view__sidebar">
                <DiscussionListUsersModal v-if="isControlDisabled === false && isEditMode === false" />
                <UsersForMicrophonesModal v-else
                                          :roomMapId="roomMapId"
                                          :selectedMicrophoneId="selectedMicrophoneId"
                                          :microphoneUsers="microphoneUsers"
                                          :isEditMode="isEditMode"
                                          @assign-user-to-microphone="handleAssignUserToMicrophone"
                                          @remove-user-from-microphone="handleRemoveUserFromMicrophone"
                                          @microphone-users-changed="handleMicrophoneUsersChanged" />
            </div>
            <div class="room-map-view__content">
                <section class="header">
                    <section class="header-left">
                        <section class="d-flex flex-row gap-4 align-items-center">
                            <ClockComponent class="text-light" v-if="$store.state.settings.runtime.showClockInOw" />
                        </section>
                        <section class="header-right header-right__text">
                            <section style="flex: 1;">
                                <p class="header-right__client-name"> {{this.$store.state.userStore.userData.client_name}} </p>
                                <p class="header-right__session-name"> {{getPublicSessionName(session, 999999999999)}} </p>
                            </section>
                        </section>
                    </section>
                </section>
                <div v-if="activePoint && activePoint.id > 0 && sessionStatus === true" class="room-map-active-point notice">
                    {{ $t('com.file.room-map.active-point')}}: {{ activePoint.name }}
                </div>
                <div class="room-map-wrapper">
                    <RoomMap :isControlDisabled="isControlDisabled"
                             :isEditDisabled="isEditDisabled"
                             :votingModule="votingModule"
                             :roomMapId="roomMapId"
                             :currentSpeech="currentSpeech"
                             :discussRequestList="discussRequestList"
                             :activePoint="activePoint"
                             :sessionStatus="sessionStatus"
                             @edit-mode-changed="handleEditModeChanged"
                             @start-voting="handleStartVoting"
                             @microphone-selected="handleMicrophoneSelected"
                             @microphone-users-changed="handleMicrophoneUsersChanged"
                             @user-queued="handleUserQueued"
                             @user-removed-from-queue="handleUserRemovedFromQueue"
                             @voice-granted="handleVoiceGranted"
                             @voice-revoked="handleVoiceRevoked"
                             ref="roomMap" />
                </div>
            </div>
        </div>
        <SelectAgendaItemModal v-if="isAgendaSelectVisible"
                               :agendaItems="session.agendaItems || []"
                               @close="isAgendaSelectVisible = false"
                               @confirm="startVotingForAgenda" />

        <Transition>
            <AddVotingModal v-if="$store.state.votingStore.isShowAddVotingModal && votingModule === 1 && $store.state.votingStore.isSessionEnd === false" />
        </Transition>

        <Transition>
            <InteractiveVotingModal v-if="$store.state.votingStore.isShowInteractiveVotingModal && votingModule === 1 && $store.state.votingStore.isSessionEnd === false" />
        </Transition>

        <Transition>
            <InteractiveVotingResults v-if="$store.state.votingStore.isShowInteractiveVotingResultsModal && votingModule === 1" />
        </Transition>

    </section>
    <section v-else class="room-map-view d-flex flex-row justify-content-center flex-wrap gap-2 px-3 text-center">
        <h2 style="color: #000;">{{$t('com.file.discussion-quest-list.no-discuss-mode')}} <br />{{$t('com.file.discussion-quest-list.or-no-interactive-mode')}}</h2>
    </section>
</template>

<script>
import ClockComponent from "@/components/BasicControls/ClockComponent.vue";
import RoomMap from "@/components/Panel/Interactive/RoomMap/Modals/RoomMap.vue";
import DiscussionListUsersModal from "@/components/Panel/Interactive/RoomMap/Modals/DiscussionListUsersModal.vue";
import UsersForMicrophonesModal from "@/components/Panel/Interactive/RoomMap/Modals/UsersForMicrophonesModal.vue";
import SelectAgendaItemModal from "@/components/Panel/Interactive/RoomMap/Modals/SelectAgendaItemModal.vue";
import AddVotingModal from "@/components/Panel/Content/SessionPoints/Modals/AddVotingModal.vue";
import InteractiveVotingModal from "@/components/Panel/Content/SessionPoints/Modals/InteractiveVotingModal.vue";
import InteractiveVotingResults from "@/components/Panel/Content/SessionPoints/Modals/InteractiveVotingResults.vue";
import moment from "moment";
import { getPublicSessionName } from "@/helpers/session.helper";
import { formatDateToDisplay } from "@/helpers/i18n.utils";
import AlertHelper from '@/helpers/alerts.helper.js';
import eSesjaInteractiveService from "@/services/esesja.interactive.service";
import InteractiveService from "@/services/interactive.service";
import SessionService from "@/services/session.service";
import VotingService from "@/services/voting.service";
import userService from "@/services/user.service";
import { encryptStorage } from '@/services/encrypt.service';
import roomMapService from "@/services/room.map.service";
import { debounce } from "@/helpers/utils";

export default {
    name: "RoomMapView",
    data() {
        return {
            session: {},
            error: false,
            isEditMode: false,
            users: [],
            currentSpeech: null,
            discussRequestList: [],
            discussionModule: encryptStorage.getItem('modules').d,
            roomMapModule: encryptStorage.getItem('modules').ps,
            votingModule: encryptStorage.getItem('modules').g,
            roomMapId: null,
            selectedMicrophoneId: null,
            microphoneUsers: {},
            micUserPairCyclicFlag: false,
            micUserPairDebounced: null,
            pendingMicUserPairs: null,
            sessionStatus: false,
            activePoint: {
                id: 0,
                name: ""
            },
            isAgendaSelectVisible: false,
            chartData: {
                labels: [],
                datasets: [{ data: [] }],
            },
            internalBasicData: null,
            votingPossibleAnswers: null,
        };
    },
    components: {
        ClockComponent,
        RoomMap,
        DiscussionListUsersModal,
        UsersForMicrophonesModal,
        SelectAgendaItemModal,
        AddVotingModal,
        InteractiveVotingModal,
        InteractiveVotingResults
    },

    created() {
        this.micUserPairDebounced = debounce(async () => {
            if (!this.shouldPollMicUserPairs()) return;

            this.micUserPairCyclicFlag = !this.micUserPairCyclicFlag;
            await this.fetchMicUserPairs();
        }, 2000);
    },

    updated() {
        if (this.pendingMicUserPairs && this.$refs.roomMap?.applyMicUserPairs) {
            this.$refs.roomMap.applyMicUserPairs(this.pendingMicUserPairs);
            this.microphoneUsers = { ...this.$refs.roomMap.microphoneUsers };
            this.pendingMicUserPairs = null;
        }
    },

    computed: {
        getDate() {
            if (!this.session.meeting_date) return '';
            return formatDateToDisplay(this.session.meeting_date, 'weekdayDate');
        },
        getTime() {
            if (!this.session.meeting_date) return '';
            return moment(this.session.meeting_date).format("LT");
        },
        isControlDisabled() {
            const sessionId = this.$route.params.sessionid;
            return sessionId === '0' || sessionId === 0;
        },
        isEditDisabled() {
            const sessionId = this.$route.params.sessionid;
            const parsedSessionId = typeof sessionId === 'string' ? parseInt(sessionId) : sessionId;
            return parsedSessionId > 0;
        },
        showRoomMapView() {
            const sessionId = this.$route.params.sessionid;
            const parsedSessionId = typeof sessionId === 'string' ? parseInt(sessionId) : sessionId;

            if (!this.error === false) {
                return false;
            }

            if (!parsedSessionId || parsedSessionId === 0) {
                return !this.error;
            }

            return !this.error && this.isEditMode === false && this.sessionStatus === true;
        },
        getInteractiveVotingModalInAppStatus() {
            return this.$store.state.votingStore.interactiveVotingModalInAppStatus;
        }
    },

    watch: {
        '$store.state.votingStore.saveVotingStatus': {
            deep: true,
            handler: function (newValue) {
                if (newValue && this.$route.name === "RoomMap") {
                    this.$store.commit("setSaveVotingStatus", false);
                    this.handleAddVotingRoomMap();
                }
            }
        },
        '$store.state.votingStore.startVotingStatus': {
            handler(newValue) {
                if (newValue && this.$route.name === 'RoomMap') {
                    const data = this.$store.state.votingStore.startVotingData

                    if (data) {
                        this.handleStartVotng(data.pointId, data.votingId);
                    }

                    this.$store.commit('setStartVotingStatus', false)
                }
            },
        },
        '$store.state.votingStore.isShowInteractiveVotingModal': {
            handler(newValue) {
                if (this.$route.name === 'RoomMap') {
                    this.handleInteractiveVotingModalStatus(newValue);
                }
            },
        },
        '$store.state.votingStore.saveInteractiveVotingStatus': {
            handler(newValue) {
                if (newValue && this.$route.name === 'RoomMap') {
                    const data = this.$store.state.votingStore.saveInteractiveVotingData

                    if (data) {
                        this.handleSaveVotingAction(data);
                    }

                    this.$store.commit('setSaveInteractiveVotingStatus', false);
                }
            },
        },
        '$store.state.votingStore.isResultsInVisualization': {
            handler(newValue) {
                if (this.$route.name === 'RoomMap') {
                    this.handleIsResultsInVisualization(newValue.value);
                }
            },
        },

        '$refs.roomMap': {
            handler() {
                if (this.$refs.roomMap) {
                    this.microphoneUsers = { ...this.$refs.roomMap.microphoneUsers }
                }
            },
            immediate: true,
            deep: true
        },

        micUserPairCyclicFlag() {
            this.micUserPairDebounced?.();
        },

        showRoomMapView: {
            flush: 'post',
            handler(newVal) {
                if (!newVal) {
                    this.stopMicUserPairPolling();
                    return;
                }
                this.startMicUserPairPolling();
            }
        },

        sessionStatus(newVal) {
            if (!newVal) {
                this.stopMicUserPairPolling();
            }
        },
    },

    async mounted() {
        if (this.isDiscussionModule() === false || this.isRoomMapModule() === false)
            return;

        await this.loadData();
    },

    beforeUnmount() {
        this.micUserPairDebounced?.cancel?.();
        this.disconnectFromSignalR();
        this.stopMicUserPairPolling();
    },

    methods: {
        isDiscussionModule() {
            return this.discussionModule === 1;
        },
        isRoomMapModule() {
            return this.roomMapModule === 1;
        },

        async internalFetchSessionInteractiveState(sessionId) {
            let response = await SessionService.isSessionInInteractive(sessionId);
            if (response.status !== 200) {
                throw await Promise.reject(response);
            }
            this.sessionStatus = response.data;
        },

        async getActivePoint() {
            try {
                let res = await InteractiveService.getActivePoint(this.session.id);
                if (res.status == 200 && res.data) {
                    this.activePoint = this.getPointData(res.data.pid);
                }

            } catch (e) {
                if (e.response.status == 401) {
                    this.$router.push({ name: 'Login' })
                }
                else {
                    this.$router.push({ name: 'notfoundpage', params: { pathMatch: 'roomMap' } });
                }
            }
        },

        getPointData(pointId) {
            if (pointId <= 0) {
                return { id: 0, name: "" };
            }

            const findNameById = (id, items) => {
                for (const item of items) {
                    if (item.id === id) {
                        return item.name;
                    }

                    if (item.children && item.children.length > 0) {
                        const result = findNameById(id, item.children);
                        if (result) return result;
                    }
                }
                return "";
            };

            const name = findNameById(pointId, this.session.agendaItems);

            return {
                id: pointId,
                name: name ?? ""
            };
        },

        async internalFetchSessionData(sessionId) {
            try {
                let result = await SessionService.getSessionWithPointsById(sessionId);

                if (result.status !== 200) {
                    this.session = {};
                }
                this.session = result.data;
            }
            catch (e) {
                if (e.response.status == 401) {
                    this.$router.push({ name: 'Login' })
                }
                else {
                    this.$router.push({ name: 'notfoundpage', params: { pathMatch: 'roomMap' } });
                }
            }
        },

        async getCurrentRequestList() {
            if (this.isDiscussionModule()) {
                try {
                    let res = await InteractiveService.getCurrentRequestList(this.session.id);
                    if (res.status == 200 && res.data) {
                        this.mapDiscussionDataToState(res.data);
                    }
                }
                catch (e) {
                    var t = this.$swal.mixin(AlertHelper.errorToastElement());
                    t.fire(AlertHelper.errorToastAlert(this.$t('com.validation-error.get-discuss-list-error'), this.$t('com.global.error')));
                    this.$router.push({ name: 'notfoundpage', params: { pathMatch: 'roomMap' } });
                }
            }
        },

        async checkClientRoomMapForSession(roomMapId, sessionId) {
            if (this.isDiscussionModule()) {
                try {
                    await roomMapService.checkClientRoomMapForSession(roomMapId, sessionId);
                }
                catch (e) {
                    this.$router.push({ name: 'notfoundpage', params: { pathMatch: 'roomMap' } });
                }
            }
        },

        mapDiscussionDataToState(discussionData) {
            if (discussionData) {
                this.currentSpeech = discussionData.currentSpeech || null;
                this.discussRequestList = discussionData.discussRequestList || [];
            }
        },

        async loadData() {
            try {
                this.$store.commit("showLoader");
                this.error = false;
                const roomMapId = this.$route.params.roomMapId;
                const sessionId = this.$route.params.sessionid;

                this.roomMapId = roomMapId;

                await this.checkClientRoomMapForSession(roomMapId, sessionId);

                if (sessionId !== '0' && sessionId !== 0) {
                    await this.internalFetchSessionInteractiveState(sessionId);
                }

                if (sessionId !== '0' && sessionId !== 0) {
                    await this.internalFetchSessionData(sessionId);
                    await this.getActivePoint();
                }

                if (sessionId !== '0' && sessionId !== 0) {
                    await this.getCurrentRequestList();
                }

                await this.$store.dispatch('roomMap/setRoomMapId', roomMapId);

                if (Object.keys(this.session).length > 0 &&
                    sessionId &&
                    this.session.id === parseInt(sessionId) &&
                    sessionId > 0 &&
                    this.sessionStatus == true
                ) {
                    await this.connectToSignalR();
                }

            } catch (err) {
                console.error('Error loading data:', err);
                this.error = true;
            }
            finally {
                this.$store.commit("hideLoader");
            }
        },

        getPublicSessionName,

        handleEditModeChanged(isEditMode) {
            this.isEditMode = isEditMode

            if (this.$refs.roomMap) {
                this.microphoneUsers = { ...this.$refs.roomMap.microphoneUsers }
                this.selectedMicrophoneId = this.$refs.roomMap.selectedMicrophoneId
            }
        },

        handleStartVoting(agendaId) {
            if (this.isControlDisabled) return;

            if (this.getInteractiveVotingModalInAppStatus === true) return;

            if (this.votingModule === 1) {
                if (agendaId) {
                    this.startVotingForAgenda(agendaId);
                } else {
                    this.isAgendaSelectVisible = true;
                }
            }
        },

        startVotingForAgenda(agendaId) {
            if (this.votingModule === 1) {
                this.isAgendaSelectVisible = false;

                this.$store.commit("setIsSessionEndStatus", this.session.is_end);
                this.$store.commit("setIsRoomMapAddVotingCallStatus", true);
                this.$store.commit("setIsCopyVotingStatus", false);
                this.$store.commit("setSelectedVotingPointId", this.getPointData(agendaId));
                this.$store.commit("setSelectedVotingId", null);
                this.$store.commit("setVotingSessionId", this.session.id);
                this.$store.commit("setVotingMode", "standard");
                this.$store.commit("setIsShowAddVotingModalStatus", true);
            }
        },

        handleMicrophoneSelected(micId) {
            this.selectedMicrophoneId = micId
        },

        handleMicrophoneUsersChanged() {
            if (this.$refs.roomMap) {
                this.microphoneUsers = { ...this.$refs.roomMap.microphoneUsers }
            }
        },

        async handleAssignUserToMicrophone(micId, userId) {
            if (this.$refs.roomMap) {
                await this.$refs.roomMap.assignUserToMicrophone(micId, userId)
                this.microphoneUsers = { ...this.$refs.roomMap.microphoneUsers }
            }
        },

        async handleRemoveUserFromMicrophone(micId, userId) {
            if (this.$refs.roomMap) {
                await this.$refs.roomMap.removeUserFromMicrophone(micId, userId)
                this.microphoneUsers = { ...this.$refs.roomMap.microphoneUsers }
            }
        },

        async connectToSignalR() {
            try {
                let responseRid = await userService.getCurrentRid();

                if (responseRid.status !== 200) {
                    throw await Promise.reject(responseRid);
                }

                await eSesjaInteractiveService.start(responseRid);
                await eSesjaInteractiveService.joinRoom(this.session.id);

                this.registerSignalREvents();
                this.startMicUserPairPolling();
                this.$store.commit("turnOnInteractive");
            } catch (error) {
                console.error('Błąd podczas łączenia z SignalR:', error);
            }
        },

        disconnectFromSignalR() {
            try {
                this.unregisterSignalREvents();
                this.stopMicUserPairPolling();

                eSesjaInteractiveService.leaveRoom(this.session.id);
                eSesjaInteractiveService.stop();
                this.$store.commit("turnOffInteractive");
            } catch (error) {
                console.error('Błąd podczas rozłączania z SignalR:', error);
            }
        },

        shouldPollMicUserPairs() {
            return this.roomMapId && !this.isControlDisabled && this.showRoomMapView;
        },

        startMicUserPairPolling() {
            if (!this.shouldPollMicUserPairs()) return;
            this.micUserPairCyclicFlag = !this.micUserPairCyclicFlag;
        },

        stopMicUserPairPolling() {
            this.micUserPairDebounced?.cancel?.();
            this.micUserPairCyclicFlag = false;
        },

        async fetchMicUserPairs() {
            if (!this.shouldPollMicUserPairs()) return;

            try {
                const res = await roomMapService.getMicUserPair(this.roomMapId);

                if (!this.shouldPollMicUserPairs()) return;

                const pairs = res.data?.value || (Array.isArray(res.data) ? res.data : null);
                
                if (res.status === 200 && Array.isArray(pairs)) {
                    this.pendingMicUserPairs = pairs;

                    if (this.$refs.roomMap?.applyMicUserPairs) {
                        this.$refs.roomMap.applyMicUserPairs(pairs);
                        this.microphoneUsers = { ...this.$refs.roomMap.microphoneUsers };
                        this.pendingMicUserPairs = null;
                    }
                }
            } catch (error) {
                console.error('Błąd podczas odświeżania par mic-user:', error);
            }
        },

        registerSignalREvents() {
            eSesjaInteractiveService.events.on("SignalRUpdateDiscussionListAdmin", this.handleUpdateDiscussionListAdmin);
            eSesjaInteractiveService.events.on("SignalRInteractiveSessionStateChange", this.internalInteractiveSessionState);
            eSesjaInteractiveService.events.on("SignalRActivePointRoomMapChange", this.activePointChange);
            eSesjaInteractiveService.events.on("SignalRSessionDataRoomMapChange", this.sessionDataChange);
            eSesjaInteractiveService.events.on("SignalRInteraactiveVotingModalStatusChange", this.InteraactiveVotingModalStatusChange);
        },

        unregisterSignalREvents() {
            eSesjaInteractiveService.events.removeListener("SignalRUpdateDiscussionListAdmin", this.handleUpdateDiscussionListAdmin);
            eSesjaInteractiveService.events.removeListener("SignalRInteractiveSessionStateChange", this.internalInteractiveSessionState);
            eSesjaInteractiveService.events.removeListener("SignalRActivePointRoomMapChange", this.activePointChange);
            eSesjaInteractiveService.events.removeListener("SignalRSessionDataRoomMapChange", this.sessionDataChange);
            eSesjaInteractiveService.events.removeListener("SignalRInteraactiveVotingModalStatusChange", this.InteraactiveVotingModalStatusChange);
        },

        async sessionDataChange(sessionId) {
            try {
                if (sessionId !== '0' && sessionId !== 0) {
                    await this.internalFetchSessionData(sessionId);
                    await this.getActivePoint();
                }
            } catch (e) {
                console.log(e)
            }
        },

        InteraactiveVotingModalStatusChange(status) {
            this.$store.commit("setInteractiveVotingModalInAppStatus", status);
        },

        activePointChange(data) {
            this.activePoint = data;
        },

        internalInteractiveSessionState(status) {
            this.sessionStatus = status;
        },

        handleUpdateDiscussionListAdmin(data) {
            try {
                const discussionData = data?.discussion || data;
                if (discussionData && (discussionData.currentSpeech || discussionData.discussRequestList)) {
                    this.mapDiscussionDataToState(discussionData);
                }
            } catch (error) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.validation-error.get-discuss-list-error'), this.$t('com.global.error')));
            }
        },

        getUserDiscussionId(userId) {
            if (this.discussRequestList && Array.isArray(this.discussRequestList)) {
                const request = this.discussRequestList.find(r => {
                    const requestUserId = typeof r.userId === 'number' ? r.userId : parseInt(r.userId)
                    const compareUserId = typeof userId === 'number' ? userId : parseInt(userId)
                    return requestUserId === compareUserId
                })
                if (request && request.discussionId) {
                    return typeof request.discussionId === 'number' ? request.discussionId : parseInt(request.discussionId)
                }
            }

            if (this.currentSpeech && this.currentSpeech.userId && this.currentSpeech.discussionId) {
                const speechUserId = typeof this.currentSpeech.userId === 'number' ? this.currentSpeech.userId : parseInt(this.currentSpeech.userId)
                const compareUserId = typeof userId === 'number' ? userId : parseInt(userId)
                if (speechUserId === compareUserId) {
                    return typeof this.currentSpeech.discussionId === 'number' ? this.currentSpeech.discussionId : parseInt(this.currentSpeech.discussionId)
                }
            }

            return -1
        },

        async handleUserRemovedFromQueue(userId) {
            if (this.isControlDisabled) return;

            const discussionId = this.getUserDiscussionId(userId)

            try {
                await eSesjaInteractiveService.removeUserFromRequestSpeech(userId, this.session.id, discussionId);
            } catch (error) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.validation-error.delete-from-discuss-list-error'), this.$t('com.global.error')));
            }
        },

        async handleUserQueued(userId, type, handle) {
            if (this.isControlDisabled) return;

            try {
                await eSesjaInteractiveService.addUserToRequestSpeech(userId, this.session.id, type, handle);
            } catch (error) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.validation-error.add-to-discuss-list-error'), this.$t('com.global.error')));
            }
        },

        async handleVoiceGranted(userId) {
            if (this.isControlDisabled) return;

            const discussionId = this.getUserDiscussionId(userId)

            try {
                await eSesjaInteractiveService.giveUserSpeech(userId, discussionId, this.session.id, 0);
            } catch (error) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.validation-error.get-voice-for-error'), this.$t('com.global.error')));
            }
        },

        async handleVoiceRevoked(userId) {
            if (this.isControlDisabled) return;

            const discussionId = this.getUserDiscussionId(userId)

            try {
                await eSesjaInteractiveService.endUserSpeech(userId, discussionId, this.session.id);
            } catch (error) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.validation-error.end-voice-error'), this.$t('com.global.error')));
            }
        },

        async handleAddVotingRoomMap() {
            if (this.isControlDisabled) return;
            if (this.votingModule !== 1) return;

            try {
                await eSesjaInteractiveService.reloadSessionAfterAddVotingRoomMap(this.session.id);
            } catch (error) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.file.add-voting-modal.add-voting'), this.$t('com.global.error')));
            }
        },

        async handleStartVotng(pointId, votingId) {
            if (this.isControlDisabled) return;
            if (this.votingModule !== 1) return;

            let point = {
                id: pointId
            };

            this.$store.commit("setVotingOpenAutomaticStatus", false);
            this.$store.commit("setSelectedVotingPointId", point);
            this.$store.commit("setSelectedVotingId", votingId);
            this.$store.commit("setVotingSessionId", this.session.id);
            this.$store.commit("setIsRoomMapInteractiveVotingCallStatus", true);
            this.$store.commit("setIsShowInteractiveVotingModalStatus", true);
        },

        async handleInteractiveVotingModalStatus(status) {
            try {
                await eSesjaInteractiveService.setInteraactiveVotingModalStatus(this.session.id, status);
            } catch (error) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.file.add-voting-modal.add-voting'), this.$t('com.global.error')));
            }
        },

        async handleSaveVotingAction(data) {
            if (this.isControlDisabled) return;
            if (this.votingModule !== 1) return;

            try {
                this.$store.commit("showLoader");
                let result = await VotingService.getVotingInteractiveDataForResults(this.session.id, data.pointId, data.votingId)

                if (result.status == 200) {
                    this.internalBasicData = result.data;

                    var resultVoting = (await VotingService.getVotingsSummaryResults(data.votingId, this.internalBasicData.mode.id, this.internalBasicData.isPublic, this.internalBasicData.gType));
                    if (resultVoting.status == 200) {
                        this.votingPossibleAnswers = resultVoting.data;

                        if (this.internalBasicData.gType != 4) {
                            var tempdata = [];

                            if (this.votingPossibleAnswers != null) {
                                this.chartData.datasets[0].backgroundColor = []
                                for (var i = 0; i < this.votingPossibleAnswers.options.length; i++) {
                                    this.chartData.labels.push(this.votingPossibleAnswers.options[i].option);
                                    tempdata.push(this.votingPossibleAnswers.options[i].count);
                                    this.chartData.datasets[0].data = tempdata;
                                    this.chartData.datasets[0].backgroundColor.push(this.formatAnswersColorChart(this.votingPossibleAnswers.options[i].option));
                                }
                            }
                        }
                    }
                    this.$store.commit("setCanCloseStatus", true);
                    this.$store.commit("setIsPublicViewCallStatus", false);
                    this.$store.commit("setVotingChartData", this.chartData);
                    this.$store.commit("setVotingInternalBasicData", this.internalBasicData);
                    this.$store.commit("setVotingPossibleAnswersData", this.votingPossibleAnswers);
                    this.$store.commit("setIsShowInteractiveVotingResultsModalStatus", true);
                }
                await eSesjaInteractiveService.reloadSessionAfterAddVotingRoomMap(this.session.id);

            } catch (e) {
                if (e.response.status == 401)
                    this.$router.push({ name: 'Login' })
            }
            finally {
                this.$store.commit("hideLoader");
            }
        },

        async handleIsResultsInVisualization(status) {
            if (this.sessionStatus === true) {
                if (status) {
                    await eSesjaInteractiveService.openResultVotingModal(this.session.id, this.votingPossibleAnswers, this.internalBasicData);
                }
                else {
                    await eSesjaInteractiveService.closeResultVotingModal(this.session.id);
                }
            }
        },

        formatAnswersColorChart(result) {
            if (result != null && result.length > 0) {
                switch (result) {
                    case this.$t('com.file.interactive-voting-public-modal.vote-for'):
                    case this.$t('com.file.interactive-voting-public-modal.vote-yes'):
                        return '#79e563';
                    case this.$t('com.file.interactive-voting-public-modal.vote-against'):
                    case this.$t('com.file.interactive-voting-public-modal.vote-no'):
                        return '#e40030';
                    case this.$t('com.file.interactive-voting-public-modal.vote-abstain'):
                    case this.$t('com.file.interactive-voting-public-modal.vote-dont-know'):
                        return '#ffce56';
                    case this.$t('com.file.interactive-voting-public-modal.vote-lack'):
                        return '#c0c0c0';
                    case this.$t('com.file.interactive-voting-public-modal.vote-absent-prular'):
                    case this.$t('com.file.interactive-voting-public-modal.vote-absent-singular'):
                        return '#000';
                    case this.$t('com.file.interactive-voting-public-modal.vote-excluded'):
                    case this.$t('com.file.interactive-voting-public-modal.vote-excluded2'):
                        return '#363532';
                    default:
                        return '#7cd1eb';
                }
            }
        },
    }
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/roommapview.scss';
</style>
